import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input } from '@cloudflare/kumo/components/input';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { EnvelopeSimple, Key } from '@phosphor-icons/react';
import { authClient, useSession, apiBaseURL } from '@/lib/authClient';

// 管理者用サインイン (2 段階: メール送信 → 6 桁コード入力 / もしくはメール内リンク)。
// public-site と同じ Better Auth instance。一般ユーザーが入っても RequireAdmin で蹴られる。
// 6 桁コードは same-device 限定。別端末で入りたい場合はメール内リンクを使う。
export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: session, isPending } = useSession();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [passkeyLoggingIn, setPasskeyLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = searchParams.get('redirect') || '/';

  // Claude.ai 等の Remote MCP connector からの OAuth 認可要求でこのページに来た場合、
  // better-auth の mcp プラグインは「セッション Cookie が新規発行された better-auth の
  // 任意のレスポンス」をトップレベルリダイレクトに差し替える仕組みを持つが、これは実ブラウザ
  // ナビゲーション (Magic Link のメールリンク) でしか機能しない。6桁コード入力・Passkey は
  // fetch() ベースのため、ログイン成功後に SPA 内遷移する代わりに /mcp/authorize を
  // フルページ遷移で再訪させ、正規の 302 リダイレクトを発生させる必要がある。
  const isOAuthFlow = searchParams.has('client_id') && searchParams.has('response_type');
  const oauthReplayUrl = `${apiBaseURL}/api/auth/mcp/authorize?${window.location.search.slice(1)}`;

  useEffect(() => {
    if (!isPending && session?.user) {
      if (isOAuthFlow) {
        window.location.assign(oauthReplayUrl);
        return;
      }
      navigate(redirectTo, { replace: true });
    }
  }, [isPending, session, navigate, redirectTo, isOAuthFlow, oauthReplayUrl]);

  // Conditional UI: input にカーソルを置いたとき OS の Passkey ピッカーが出る (メール段階のみ)
  useEffect(() => {
    if (isPending || session?.user || step !== 'email') return;
    let cancelled = false;
    (async () => {
      try {
        await authClient.signIn.passkey({ autoFill: true });
      } catch {
        // autoFill は失敗時に静かに無視
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [isPending, session, step]);

  const handlePasskeyLogin = async () => {
    setPasskeyLoggingIn(true);
    setError(null);
    try {
      const result = await authClient.signIn.passkey();
      if (result.error) {
        setError(result.error.message ?? 'パスキーログインに失敗しました');
      }
    } catch (err) {
      console.error('Passkey login failed:', err);
      setError('パスキーログインに失敗しました');
    } finally {
      setPasskeyLoggingIn(false);
    }
  };

  const sendLoginEmail = async (): Promise<boolean> => {
    const callbackURL = new URL(redirectTo, window.location.origin).toString();
    const { error: authError } = await authClient.signIn.magicLink({ email, callbackURL });
    if (authError) {
      setError(authError.message ?? 'ログインメールの送信に失敗しました');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (await sendLoginEmail()) {
        setOtp('');
        setStep('code');
      }
    } catch (err) {
      console.error('Magic link send failed:', err);
      setError('ログインメールの送信に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);
    try {
      await sendLoginEmail();
    } catch (err) {
      console.error('Magic link resend failed:', err);
      setError('再送に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      setError('6 桁の確認コードを入力してください');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const { error: authError } = await authClient.signIn.emailOtp({ email, otp });
      if (authError) {
        if (authError.status === 403) {
          setError(
            'このブラウザでは確認コードを使えません。お手数ですが、メール内のリンクからログインしてください。'
          );
        } else {
          setError(authError.message ?? '確認コードが正しくありません。再度お試しください。');
        }
        setVerifying(false);
        return;
      }
      window.location.assign(isOAuthFlow ? oauthReplayUrl : redirectTo);
    } catch (err) {
      console.error('OTP verify failed:', err);
      // OAuth フロー中は better-auth 側のフックがレスポンスをクロスオリジンの
      // redirect_uri への 302 に差し替えるため、fetch() がそのリダイレクトを
      // 追従できず例外になる (=OTP 自体は成功しセッション Cookie は確立済み)。
      // ここで一律エラー表示すると、実際は成功しているのに失敗したと誤解させるため、
      // /mcp/authorize をフルページ遷移で再訪させて正規の 302 を発生させる。
      if (isOAuthFlow) {
        window.location.assign(oauthReplayUrl);
        return;
      }
      setError('確認に失敗しました。時間をおいて再度お試しください。');
      setVerifying(false);
    }
  };

  if (step === 'code') {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-4 px-6 py-16">
        <Surface className="flex flex-col gap-4 p-6">
          <form onSubmit={handleVerify} className="flex flex-col gap-4">
            <h1 className="text-xl font-semibold text-kumo-default">確認コードを入力</h1>
            <p className="text-sm text-kumo-default">
              <strong>{email}</strong> 宛にメールを送信しました。
              メールに記載された <strong>6 桁の確認コード</strong> を入力してください。
            </p>

            {error ? <Banner variant="error">{error}</Banner> : null}

            <Field label="確認コード">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
                disabled={verifying}
              />
            </Field>

            <Button type="submit" variant="primary" loading={verifying}>
              ログイン
            </Button>

            <Banner variant="alert">
              メール内のリンクをクリックしてもログインできます。別の端末・ブラウザでログインする場合は、
              コードではなくリンクをご利用ください。確認コードの有効期限は <strong>10 分間</strong> です。
            </Banner>

            <div className="flex items-center justify-between">
              <Button type="button" variant="ghost" size="sm" loading={resending} onClick={handleResend}>
                コードを再送
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep('email');
                  setOtp('');
                  setError(null);
                }}
              >
                別のメールアドレスで送り直す
              </Button>
            </div>
          </form>
        </Surface>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-4 px-6 py-16">
      <Surface className="flex flex-col gap-4 p-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <h1 className="text-xl font-semibold text-kumo-default">管理者ログイン</h1>
          <p className="text-sm text-kumo-muted">ご登録の管理者メールアドレスを入力してください。</p>

          {error ? <Banner variant="error">{error}</Banner> : null}

          <Field label="メールアドレス">
            <Input
              type="email"
              placeholder="admin@example.com"
              autoComplete="email webauthn"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
            />
          </Field>

          <Button type="submit" variant="primary" icon={EnvelopeSimple} loading={submitting}>
            確認コードをメールで受け取る
          </Button>

          <div className="my-1 flex items-center gap-3 text-xs text-kumo-muted">
            <span className="h-px flex-1 bg-kumo-line" />
            または
            <span className="h-px flex-1 bg-kumo-line" />
          </div>

          <Button variant="secondary" icon={Key} loading={passkeyLoggingIn} onClick={handlePasskeyLogin}>
            パスキーでログイン
          </Button>
        </form>
      </Surface>
    </div>
  );
}
